import React, { useState, useEffect, useCallback } from 'react';

function decodeToken(token) {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

const ALPHANUMERIC_RE = /^[A-Za-z0-9-]*$/;

function getCurrentFiscalYear() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  return String(month >= 9 ? year + 1 : year);
}

function buildFiscalYears(startYear = 2019, endYear = Number(getCurrentFiscalYear()) + 4) {
  const years = [];
  for (let year = startYear; year <= endYear; year += 1) {
    years.push(String(year));
  }
  return years;
}

const CURRENT_FISCAL_YEAR = getCurrentFiscalYear();
const FISCAL_YEARS = buildFiscalYears();
const NEW_PROGRAM_FISCAL_YEAR_OPTIONS = [
  CURRENT_FISCAL_YEAR,
  String(Number(CURRENT_FISCAL_YEAR) + 1),
  String(Number(CURRENT_FISCAL_YEAR) + 2),
];
const PROJECT_TYPES = [
  'Adult Employment',
  'Community College Partnership',
  'Community Service Employment',
  'Dislocated Worker',
  'Incumbent Worker Training',
  'On-the-Job Training',
  'Performance-Based Contract',
  'Reentry Employment',
  'Registered Apprenticeship',
  'Rural Employment Initiative',
  'Skills Upgrade Training',
  'Youth Apprenticeship',
];

const STATUS_COLORS = {
  Active: 'var(--color-success)',
  Inactive: '#6b7280',
};

async function readResponsePayload(response) {
  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

const EMPTY_FORM = {
  programCode: '',
  description: '',
  fiscalYear: CURRENT_FISCAL_YEAR,
};
const EMPTY_ADD_TYPE_FORM = {
  fiscalYear: '',
  projectType: '',
  networkPath: '',
  fundingOpportunityNumber: '',
  publicLawNumber: '',
};

function getYearRange(programTypes) {
  const years = (programTypes || [])
    .map((pt) => parseInt(pt.fiscalYear, 10))
    .filter((n) => !isNaN(n));
  if (!years.length) return '—';
  const min = Math.min(...years);
  const max = Math.max(...years);
  return min === max ? String(min) : `${min} – ${max}`;
}

const SortIndicator = ({ col, sortCol, sortDir }) => {
  if (sortCol !== col) return <span className="sort-indicator sort-inactive">&#8597;</span>;
  if (sortDir === 'asc') return <span className="sort-indicator">&#8593;</span>;
  return <span className="sort-indicator">&#8595;</span>;
};

const PencilIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const ProgramsPage = () => {
  const [programs, setPrograms] = useState([]);
  const [fetchError, setFetchError] = useState('');

  const [statusFilter, setStatusFilter] = useState('');
  const [yearFilter, setYearFilter] = useState(CURRENT_FISCAL_YEAR);

  const [sortCol, setSortCol] = useState('');
  const [sortDir, setSortDir] = useState('asc');

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formFoaFile, setFormFoaFile] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [selectedProgram, setSelectedProgram] = useState(null);
  const [linkedOpportunityMeta, setLinkedOpportunityMeta] = useState(null);
  const [detailForm, setDetailForm] = useState({ programCode: '', description: '', status: 'Active' });
  const [detailFoaFile, setDetailFoaFile] = useState(null);
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);

  const [showAddTypeForm, setShowAddTypeForm] = useState(false);
  const [addTypeForm, setAddTypeForm] = useState(EMPTY_ADD_TYPE_FORM);
  const [addTypeErrors, setAddTypeErrors] = useState({});
  const [editingTypeId, setEditingTypeId] = useState(null);

  const [availableLaws, setAvailableLaws] = useState([]);
  const [linkedLawIds, setLinkedLawIds] = useState([]);
  const [lawsSelectValue, setLawsSelectValue] = useState('');
  const [lawsSaveError, setLawsSaveError] = useState('');

  const token = localStorage.getItem('token');
  const payload = token ? decodeToken(token) : null;
  const roles = payload
    ? Array.isArray(payload.roles) ? payload.roles : [payload.role].filter(Boolean)
    : [];
  const canManage = roles.includes('Super Admin') || roles.includes('Grant Manager');

  const loadPrograms = useCallback(async () => {
    try {
      const res = await fetch('/api/programs', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await readResponsePayload(res);
      if (!res.ok) throw new Error('Failed to load programs');
      setPrograms(data);
      setFetchError('');
      return data;
    } catch {
      setFetchError('Could not load programs.');
      return [];
    }
  }, [token]);

  useEffect(() => {
    if (token) loadPrograms();
  }, [token, loadPrograms]);

  useEffect(() => {
    const fetchLaws = async () => {
      try {
        const res = await fetch('/api/laws', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        setAvailableLaws(await res.json());
      } catch { }
    };
    if (token) fetchLaws();
  }, [token]);

  const availableYears = [
    ...new Set(
      [...programs.flatMap((p) => (p.programTypes || []).map((pt) => pt.fiscalYear)), CURRENT_FISCAL_YEAR]
    ),
  ].sort((a, b) => b - a);

  const filtered = programs.filter((p) => {
    if (statusFilter && p.status !== statusFilter) return false;
    if (yearFilter) {
      const hasYear = (p.programTypes || []).some((pt) => pt.fiscalYear === yearFilter);
      if (!hasYear) return false;
    }
    return true;
  });

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const sorted = [...filtered].sort((a, b) => {
    if (!sortCol) return 0;
    if (sortCol === 'yearRange') {
      const aMin = Math.min(
        ...(a.programTypes || []).map((pt) => parseInt(pt.fiscalYear, 10)).filter((n) => !isNaN(n))
      );
      const bMin = Math.min(
        ...(b.programTypes || []).map((pt) => parseInt(pt.fiscalYear, 10)).filter((n) => !isNaN(n))
      );
      const av = isFinite(aMin) ? aMin : 0;
      const bv = isFinite(bMin) ? bMin : 0;
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    }
    const av = String(a[sortCol] ?? '').toLowerCase();
    const bv = String(b[sortCol] ?? '').toLowerCase();
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const handleRowClick = (program) => {
    setSelectedProgram(program);
    setDetailForm({
      programCode: program.programCode || '',
      description: program.description || program.name || '',
      status: program.status || 'Active',
    });
    setLinkedLawIds(program.laws || []);
    setLawsSelectValue('');
    setLawsSaveError('');
    setSubmitError('');
    setShowForm(false);
    setShowAddTypeForm(false);
    setAddTypeForm(EMPTY_ADD_TYPE_FORM);
    setAddTypeErrors({});
    setEditingTypeId(null);
    setDetailFoaFile(null);
  };

  const handleCancelDetail = () => {
    setSelectedProgram(null);
    setLinkedOpportunityMeta(null);
    setDetailForm({ programCode: '', description: '', status: 'Active' });
    setLinkedLawIds([]);
    setLawsSelectValue('');
    setLawsSaveError('');
    setSubmitError('');
    setShowAddTypeForm(false);
    setAddTypeForm(EMPTY_ADD_TYPE_FORM);
    setAddTypeErrors({});
    setEditingTypeId(null);
    setDetailFoaFile(null);
  };

  const handleDetailFormChange = (e) => {
    const { name, value } = e.target;
    setDetailForm((prev) => ({ ...prev, [name]: value }));
  };

  const syncSelectedProgramFromList = (programsList, programId) => {
    const refreshedProgram = programsList.find((program) => String(program.id) === String(programId)) || null;
    if (!refreshedProgram) {
      handleCancelDetail();
      return;
    }

    setSelectedProgram(refreshedProgram);
    setDetailForm({
      programCode: refreshedProgram.programCode || '',
      description: refreshedProgram.description || refreshedProgram.name || '',
      status: refreshedProgram.status || 'Active',
    });
    setLinkedLawIds(refreshedProgram.laws || []);
  };

  useEffect(() => {
    const loadLinkedOpportunity = async () => {
      if (!selectedProgram?.linkedOpportunityId || !token) {
        setLinkedOpportunityMeta(null);
        return;
      }

      try {
        const response = await fetch(`/api/nofo/${selectedProgram.linkedOpportunityId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const body = await readResponsePayload(response);
        if (!response.ok) {
          throw new Error(body?.message || 'Failed to load linked NOFO details.');
        }
        setLinkedOpportunityMeta(body);
      } catch (loadError) {
        console.error('Failed to load linked opportunity:', loadError);
        setLinkedOpportunityMeta(null);
      }
    };

    loadLinkedOpportunity();
  }, [selectedProgram?.linkedOpportunityId, token]);

  const uploadAndPersistFoa = async ({ file, programId, existingOpportunityId = '' }) => {
    const uploadForm = new FormData();
    uploadForm.append('file', file);

    const extractResponse = await fetch('/api/nofo/upload', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: uploadForm,
    });
    const extractBody = await readResponsePayload(extractResponse);
    if (!extractResponse.ok) {
      throw new Error(extractBody.message || 'Failed to extract the NOFO.');
    }

    const saveResponse = await fetch(
      existingOpportunityId ? `/api/nofo/reimport/${existingOpportunityId}/confirm` : '/api/nofo/save',
      {
        method: existingOpportunityId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(
          existingOpportunityId
            ? extractBody.fields
            : {
                ...extractBody.fields,
                programId,
              }
        ),
      }
    );
    const saveBody = await readResponsePayload(saveResponse);
    if (!saveResponse.ok) {
      throw new Error(saveBody.message || 'Failed to save the NOFO.');
    }

    const opportunity = saveBody.opportunity || null;
    if (opportunity?.id) {
      const documentForm = new FormData();
      documentForm.append('file', file);

      const documentResponse = await fetch(`/api/nofo/${opportunity.id}/document`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: documentForm,
      });
      const documentBody = await readResponsePayload(documentResponse);
      if (!documentResponse.ok) {
        throw new Error(documentBody?.message || 'Failed to save the NOFO document.');
      }
    }

    return opportunity;
  };

  const handleOpenLinkedFoaDocument = async () => {
    if (!selectedProgram?.linkedOpportunityId) return;

    try {
      setSubmitError('');
      const response = await fetch(`/api/nofo/${selectedProgram.linkedOpportunityId}/document`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const body = await readResponsePayload(response);
        throw new Error(body?.message || 'Unable to open the linked NOFO document.');
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (openError) {
      setSubmitError(openError.message || 'Unable to open the linked NOFO document.');
    }
  };

  const handleUpdate = async () => {
    const trimmedProgramCode = detailForm.programCode.trim();
    const trimmedDescription = detailForm.description.trim();
    if (!trimmedProgramCode || !trimmedDescription) return;

    try {
      setSaveInProgress(true);
      setSubmitError('');
      setLawsSaveError('');

      const res = await fetch(`/api/programs/${selectedProgram.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          programCode: trimmedProgramCode,
          description: trimmedDescription,
          status: detailForm.status,
          laws: linkedLawIds,
          programTypes: selectedProgram.programTypes || [],
        }),
      });
      const data = await readResponsePayload(res);
      if (!res.ok) {
        setSubmitError(data.message || 'Failed to update program.');
        return;
      }

      if (detailFoaFile) {
        await uploadAndPersistFoa({
          file: detailFoaFile,
          programId: data.id,
          existingOpportunityId: data.linkedOpportunityId || '',
        });
      }

      const refreshedPrograms = await loadPrograms();
      syncSelectedProgramFromList(refreshedPrograms, data.id);
      setDetailFoaFile(null);
      setSubmitError('');
      setSuccessMessage('Program updated successfully');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch {
      setSubmitError('Could not connect to server.');
    } finally {
      setSaveInProgress(false);
    }
  };

  const handleAddTypeFormChange = (e) => {
    const { name, value } = e.target;
    setAddTypeForm((prev) => ({ ...prev, [name]: value }));
    if (addTypeErrors[name]) setAddTypeErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateAddType = () => {
    const errors = {};
    if (!addTypeForm.fiscalYear) errors.fiscalYear = 'Fiscal Year is required.';
    if (!addTypeForm.projectType) errors.projectType = 'Project Type is required.';
    if (addTypeForm.fundingOpportunityNumber && !ALPHANUMERIC_RE.test(addTypeForm.fundingOpportunityNumber)) {
      errors.fundingOpportunityNumber = 'Must be alphanumeric (hyphens allowed).';
    }
    if (addTypeForm.publicLawNumber && !ALPHANUMERIC_RE.test(addTypeForm.publicLawNumber)) {
      errors.publicLawNumber = 'Must be alphanumeric (hyphens allowed).';
    }
    return errors;
  };

  const handleEditProgramType = (programType) => {
    setEditingTypeId(programType.id);
    setAddTypeForm({
      fiscalYear: programType.fiscalYear || '',
      projectType: programType.projectType || '',
      networkPath: programType.networkPath || '',
      fundingOpportunityNumber: programType.fundingOpportunityNumber || '',
      publicLawNumber: programType.publicLawNumber || '',
    });
    setAddTypeErrors({});
    setShowAddTypeForm(true);
  };

  const handleSaveAddType = () => {
    const errors = validateAddType();
    if (Object.keys(errors).length > 0) {
      setAddTypeErrors(errors);
      return;
    }

    const programTypes = selectedProgram.programTypes || [];
    const savedType = {
      id: editingTypeId || Date.now(),
      projectType: addTypeForm.projectType,
      fiscalYear: addTypeForm.fiscalYear,
      fundingOpportunityNumber: addTypeForm.fundingOpportunityNumber.trim(),
      networkPath: addTypeForm.networkPath.trim(),
      publicLawNumber: addTypeForm.publicLawNumber.trim(),
    };

    const nextProgramTypes = editingTypeId
      ? programTypes.map((programType) =>
          programType.id === editingTypeId ? savedType : programType
        )
      : [...programTypes, savedType];

    setSelectedProgram((prev) => ({
      ...prev,
      programTypes: nextProgramTypes,
    }));
    setShowAddTypeForm(false);
    setAddTypeForm(EMPTY_ADD_TYPE_FORM);
    setAddTypeErrors({});
    setEditingTypeId(null);
  };

  const handleCancelAddType = () => {
    setShowAddTypeForm(false);
    setAddTypeForm(EMPTY_ADD_TYPE_FORM);
    setAddTypeErrors({});
    setEditingTypeId(null);
  };

  const handleAddLaw = () => {
    if (!lawsSelectValue) return;
    const id = parseInt(lawsSelectValue, 10);
    if (!linkedLawIds.includes(id)) {
      setLinkedLawIds((prev) => [...prev, id]);
    }
    setLawsSelectValue('');
  };

  const handleRemoveLaw = (id) => {
    setLinkedLawIds((prev) => prev.filter((l) => l !== id));
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const errors = {};
    if (!form.programCode.trim()) errors.programCode = 'Program Code is required.';
    if (!form.fiscalYear) errors.fiscalYear = 'Fiscal Year is required.';
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    try {
      setSaveInProgress(true);
      const res = await fetch('/api/programs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(form),
      });
      const data = await readResponsePayload(res);
      if (!res.ok) {
        setSubmitError(data.message || 'Failed to create program.');
        return;
      }
      const newProgram = data;

      if (formFoaFile) {
        await uploadAndPersistFoa({
          file: formFoaFile,
          programId: newProgram.id,
          existingOpportunityId: '',
        });
      }

      const refreshedPrograms = await loadPrograms();
      setPrograms(refreshedPrograms);
      setForm(EMPTY_FORM);
      setFormFoaFile(null);
      setFormErrors({});
      setShowForm(false);
      setSuccessMessage('Program created successfully');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch {
      setSubmitError('Could not connect to server.');
    } finally {
      setSaveInProgress(false);
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setForm(EMPTY_FORM);
    setFormFoaFile(null);
    setFormErrors({});
    setSubmitError('');
  };

  const handleDeactivateProgram = async () => {
    if (!selectedProgram) return;

    try {
      setDeleteInProgress(true);
      const res = await fetch(`/api/programs/${selectedProgram.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          programCode: detailForm.programCode.trim() || selectedProgram.programCode,
          description: detailForm.description.trim() || selectedProgram.description || selectedProgram.name,
          status: 'Inactive',
          laws: linkedLawIds,
          programTypes: selectedProgram.programTypes || [],
        }),
      });
      const data = await readResponsePayload(res);
      if (!res.ok) {
        setSubmitError(data.message || 'Failed to make the program inactive.');
        return;
      }

      const refreshedPrograms = await loadPrograms();
      syncSelectedProgramFromList(refreshedPrograms, data.id);
      setSuccessMessage('Program marked inactive successfully');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch {
      setSubmitError('Could not connect to server.');
    } finally {
      setDeleteInProgress(false);
    }
  };

  const handleDeleteProgram = async () => {
    if (!selectedProgram) return;
    if (!window.confirm(`Delete program ${selectedProgram.programCode}? This cannot be undone.`)) {
      return;
    }

    try {
      setDeleteInProgress(true);
      setSubmitError('');
      const res = await fetch(`/api/programs/${selectedProgram.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const body = await readResponsePayload(res);

      if (res.status === 409) {
        setSubmitError(body.message || 'This program cannot be deleted.');
        return;
      }

      if (!res.ok) {
        setSubmitError(body.message || 'Failed to delete program.');
        return;
      }

      await loadPrograms();
      handleCancelDetail();
      setSuccessMessage('Program deleted successfully');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch {
      setSubmitError('Could not connect to server.');
    } finally {
      setDeleteInProgress(false);
    }
  };

  const handleExportCSV = () => {
    const headers = ['Program Code', 'Program Description', 'Year Range', 'Status'];
    const rows = sorted.map((p) => [
      p.programCode,
      p.name,
      getYearRange(p.programTypes),
      p.status,
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `programs_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExportPDF = () => {
    const rows = sorted
      .map(
        (p) => `
        <tr>
          <td>${p.programCode}</td>
          <td>${p.name}</td>
          <td>${getYearRange(p.programTypes)}</td>
          <td>${p.status}</td>
        </tr>`
      )
      .join('');

    const html = `<!DOCTYPE html>
<html>
<head>
  <title>NuGrant Programs Export</title>
  <style>
    body { font-family: sans-serif; padding: 24px; color: #1e293b; }
    h2 { font-size: 1.2rem; margin-bottom: 16px; }
    p { font-size: 0.85rem; color: #64748b; margin: 0 0 16px 0; }
    table { width: 100%; border-collapse: collapse; font-size: 0.88rem; }
    th { background: #f8fafc; text-align: left; padding: 8px 12px; font-weight: 600; color: #475569; border-bottom: 2px solid #e2e8f0; }
    td { padding: 8px 12px; border-bottom: 1px solid #f1f5f9; }
  </style>
</head>
<body>
  <h2>NuGrant - Grant Programs</h2>
  <p>Exported ${new Date().toLocaleString()}</p>
  <table>
    <thead><tr><th>Program Code</th><th>Program Description</th><th>Year Range</th><th>Status</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <script>window.onload = function() { window.print(); };</script>
</body>
</html>`;

    const printWindow = window.open('', '_blank', 'width=900,height=650');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
    }
  };

  return (
    <div className="programs-page">
      {successMessage && (
        <div className="programs-success-banner" role="status">
          {successMessage}
        </div>
      )}

      {/* Filters */}
      {!selectedProgram && (
      <section className="dashboard-section">
        <div className="section-top" style={{ marginBottom: '1.25rem' }}>
          <h2>Filter Programs</h2>
          <button
            className="reset-filters-btn"
            onClick={() => { setStatusFilter(''); setYearFilter(CURRENT_FISCAL_YEAR); }}
          >
            Reset Filters
          </button>
        </div>
        <div className="programs-filters">
          <div className="filter-group">
            <label className="filter-label">Status</label>
            <select
              className="search-input filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">Fiscal Year</label>
            <select
              className="search-input filter-select"
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
            >
              <option value="">All Years</option>
              {availableYears.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      </section>
      )}

      {/* Programs list — hidden while detail panel is open */}
      {!selectedProgram && (
        <section className="dashboard-section">
          <div className="section-top">
            <h2>
              Programs <span className="results-count">({sorted.length})</span>
            </h2>
            <div className="programs-actions">
              {canManage && (
                <>
                  <button className="programs-export-btn" onClick={handleExportCSV}>
                    Export Excel (CSV)
                  </button>
                  <button className="programs-export-btn" onClick={handleExportPDF}>
                    Export PDF
                  </button>
                  <button className="programs-add-btn" onClick={() => setShowForm(true)}>
                    + Add New Program
                  </button>
                </>
              )}
            </div>
          </div>

          {fetchError && <p className="section-error">{fetchError}</p>}

          {!fetchError && (
            <div className="grants-table-wrapper">
              <table className="grants-table">
                <thead>
                  <tr>
                    <th className="sortable" onClick={() => handleSort('programCode')}>
                      Program Code <SortIndicator col="programCode" sortCol={sortCol} sortDir={sortDir} />
                    </th>
                    <th className="sortable" onClick={() => handleSort('name')}>
                      Program Description <SortIndicator col="name" sortCol={sortCol} sortDir={sortDir} />
                    </th>
                    <th className="sortable" onClick={() => handleSort('yearRange')}>
                      Year Range <SortIndicator col="yearRange" sortCol={sortCol} sortDir={sortDir} />
                    </th>
                    <th className="sortable" onClick={() => handleSort('status')}>
                      Status <SortIndicator col="status" sortCol={sortCol} sortDir={sortDir} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="no-results">
                        No programs match your filters.
                      </td>
                    </tr>
                  ) : (
                    sorted.map((p) => (
                      <tr
                        key={p.id}
                        className="programs-row-clickable"
                        onClick={() => handleRowClick(p)}
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && handleRowClick(p)}
                        role="button"
                        aria-label={`View details for ${p.programCode}`}
                      >
                        <td>{p.programCode}</td>
                        <td>{p.name}</td>
                        <td>{getYearRange(p.programTypes)}</td>
                        <td>
                          <span
                            className="status-badge"
                            style={{ backgroundColor: STATUS_COLORS[p.status] || '#6b7280' }}
                          >
                            {p.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Detail panel */}
      {selectedProgram && (
        <section className="dashboard-section">
          <h2 className="programs-detail-title">Update Program</h2>

          <div className="programs-detail-fields">
            <div className="programs-form-field">
              <label className="filter-label" htmlFor="detail-programCode">
                Program Code <span className="required-mark">(*required)</span>
              </label>
              <input
                id="detail-programCode"
                name="programCode"
                type="text"
                className="search-input"
                value={detailForm.programCode}
                onChange={handleDetailFormChange}
              />
            </div>

            <div className="programs-form-field">
              <label className="filter-label" htmlFor="detail-description">
                Program Description <span className="required-mark">(*required)</span>
              </label>
              <input
                id="detail-description"
                name="description"
                type="text"
                className="search-input"
                value={detailForm.description}
                onChange={handleDetailFormChange}
              />
            </div>

            <div className="programs-form-field">
              <label className="filter-label" htmlFor="detail-status">
                Status
              </label>
              <select
                id="detail-status"
                name="status"
                className="search-input filter-select"
                value={detailForm.status}
                onChange={handleDetailFormChange}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>

          {submitError && (
            <p className="field-error programs-submit-error">{submitError}</p>
          )}

          {/* Program Types sub-section */}
          <div className="programs-subtable-section">
            <div className="programs-subtable-header">
              <h3>Program Type(s)</h3>
              {!showAddTypeForm && (
                <button
                  type="button"
                  className="programs-add-btn programs-addtype-btn"
                  onClick={() => setShowAddTypeForm(true)}
                >
                  + Add Program Type
                </button>
              )}
            </div>

            {/* Add Program Type inline form */}
            {showAddTypeForm && (
              <div className="programs-addtype-form">
                <div className="programs-form-grid">

                  <div className="programs-form-field">
                    <label className="filter-label" htmlFor="at-fiscalYear">
                      Fiscal Year <span className="required-mark">*</span>
                    </label>
                    <select
                      id="at-fiscalYear"
                      name="fiscalYear"
                      className={`search-input filter-select${addTypeErrors.fiscalYear ? ' input-error' : ''}`}
                      value={addTypeForm.fiscalYear}
                      onChange={handleAddTypeFormChange}
                    >
                      <option value="">Select a year</option>
                      {FISCAL_YEARS.map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                    {addTypeErrors.fiscalYear && (
                      <p className="field-error">{addTypeErrors.fiscalYear}</p>
                    )}
                  </div>

                  <div className="programs-form-field">
                    <label className="filter-label" htmlFor="at-projectType">
                      Project Type <span className="required-mark">*</span>
                    </label>
                    <select
                      id="at-projectType"
                      name="projectType"
                      className={`search-input filter-select${addTypeErrors.projectType ? ' input-error' : ''}`}
                      value={addTypeForm.projectType}
                      onChange={handleAddTypeFormChange}
                    >
                      <option value="">Select a type</option>
                      {PROJECT_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                    {addTypeErrors.projectType && (
                      <p className="field-error">{addTypeErrors.projectType}</p>
                    )}
                  </div>

                  <div className="programs-form-field">
                    <label className="filter-label" htmlFor="at-fundingOpportunityNumber">
                      Funding Opportunity Number
                    </label>
                    <input
                      id="at-fundingOpportunityNumber"
                      name="fundingOpportunityNumber"
                      type="text"
                      className={`search-input${addTypeErrors.fundingOpportunityNumber ? ' input-error' : ''}`}
                      value={addTypeForm.fundingOpportunityNumber}
                      onChange={handleAddTypeFormChange}
                      placeholder="e.g. FON2024001"
                    />
                    {addTypeErrors.fundingOpportunityNumber && (
                      <p className="field-error">{addTypeErrors.fundingOpportunityNumber}</p>
                    )}
                  </div>

                  <div className="programs-form-field">
                    <label className="filter-label" htmlFor="at-publicLawNumber">
                      Public Law Number
                    </label>
                    <input
                      id="at-publicLawNumber"
                      name="publicLawNumber"
                      type="text"
                      className={`search-input${addTypeErrors.publicLawNumber ? ' input-error' : ''}`}
                      value={addTypeForm.publicLawNumber}
                      onChange={handleAddTypeFormChange}
                      placeholder="e.g. PL117001"
                    />
                    {addTypeErrors.publicLawNumber && (
                      <p className="field-error">{addTypeErrors.publicLawNumber}</p>
                    )}
                  </div>

                  <div className="programs-form-field">
                    <label className="filter-label" htmlFor="at-networkPath">
                      Network Path
                    </label>
                    <input
                      id="at-networkPath"
                      name="networkPath"
                      type="text"
                      className="search-input"
                      value={addTypeForm.networkPath}
                      onChange={handleAddTypeFormChange}
                      placeholder="\\server\share\programs or https://..."
                    />
                  </div>

                </div>

                <div className="programs-modal-actions">
                  <button type="button" className="reset-filters-btn" onClick={handleCancelAddType}>
                    Cancel
                  </button>
                  <button type="button" className="programs-add-btn" onClick={handleSaveAddType}>
                    {editingTypeId ? 'Update' : 'Save'}
                  </button>
                </div>
              </div>
            )}

            <div className="grants-table-wrapper">
              <table className="grants-table programs-subtable">
                <thead>
                  <tr>
                    <th>Project Type</th>
                    <th>Fiscal Year</th>
                    <th>Funding Opportunity Number</th>
                    <th>Network Path</th>
                    <th>Edit?</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedProgram.programTypes || []).length === 0 ? (
                    <tr>
                      <td colSpan="5" className="no-results">No program types added yet.</td>
                    </tr>
                  ) : (
                    (selectedProgram.programTypes || []).map((pt) => (
                      <tr key={pt.id}>
                        <td>{pt.projectType}</td>
                        <td>{pt.fiscalYear}</td>
                        <td>{pt.fundingOpportunityNumber}</td>
                        <td>{pt.networkPath}</td>
                        <td>
                          <button
                            type="button"
                            className="programs-edit-icon-btn"
                            title="Edit"
                            aria-label="Edit program type"
                            onClick={() => handleEditProgramType(pt)}
                          >
                            <PencilIcon />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="programs-subtable-section">
            <div className="programs-subtable-header">
              <h3>Linked Laws</h3>
            </div>
            <div className="programs-laws-chips">
              {linkedLawIds.length === 0 ? (
                <span className="programs-laws-empty">No laws linked.</span>
              ) : (
                linkedLawIds.map((id) => {
                  const law = availableLaws.find((l) => Number(l.id) === Number(id));
                  if (!law) return null;
                  return (
                    <span key={id} className="programs-law-chip">
                      {law.title}
                      <button
                        type="button"
                        className="programs-law-chip-remove"
                        onClick={() => handleRemoveLaw(id)}
                        aria-label={`Remove ${law.title}`}
                      >
                        ×
                      </button>
                    </span>
                  );
                })
              )}
            </div>
            <div className="programs-laws-add-row">
              <select
                className="search-input filter-select"
                value={lawsSelectValue}
                onChange={(e) => setLawsSelectValue(e.target.value)}
              >
                <option value="">Select a law to add...</option>
                {availableLaws
                  .filter((l) => !linkedLawIds.some((id) => Number(id) === Number(l.id)))
                  .map((l) => (
                    <option key={l.id} value={l.id}>{l.title}</option>
                  ))}
              </select>
              <button
                type="button"
                className="programs-add-btn programs-addtype-btn"
                onClick={handleAddLaw}
                disabled={!lawsSelectValue}
              >
                Add
              </button>
            </div>
            {lawsSaveError && <p className="field-error">{lawsSaveError}</p>}
          </div>

          <div className="programs-subtable-section">
            <div className="programs-subtable-header">
              <h3>Linked NOFO Document</h3>
            </div>
            <div className="programs-detail-foa">
              {selectedProgram.linkedOpportunityTitle ? (
                <div className="programs-foa-summary">
                  <strong>{selectedProgram.linkedOpportunityTitle}</strong>
                  {selectedProgram.linkedOpportunityNumber ? (
                    <span>{selectedProgram.linkedOpportunityNumber}</span>
                  ) : null}
                  {linkedOpportunityMeta?.documentFileName ? (
                    <span className="programs-inline-help">Document: {linkedOpportunityMeta.documentFileName}</span>
                  ) : null}
                </div>
              ) : (
                <p className="programs-laws-empty">No NOFO linked yet.</p>
              )}
              {selectedProgram.linkedOpportunityId ? (
                <div className="programs-detail-foa-actions">
                  <button
                    type="button"
                    className={`programs-export-btn${linkedOpportunityMeta?.documentAvailable ? '' : ' disabled'}`}
                    disabled={!linkedOpportunityMeta?.documentAvailable}
                    onClick={handleOpenLinkedFoaDocument}
                  >
                    Open NOFO Document
                  </button>
                  {!linkedOpportunityMeta?.documentAvailable ? (
                    <span className="programs-inline-help">
                      No saved NOFO PDF is available yet for this linked opportunity. Upload one below to attach it.
                    </span>
                  ) : null}
                </div>
              ) : null}
              <div className="programs-form-field">
                <label className="filter-label" htmlFor="detail-foa-upload">
                  Upload NOFO PDF
                </label>
                <input
                  id="detail-foa-upload"
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={(event) => setDetailFoaFile(event.target.files?.[0] || null)}
                />
                <span className="programs-inline-help">
                  Uploading a PDF will automatically extract and link the NOFO when you save.
                </span>
                {detailFoaFile ? (
                  <span className="programs-inline-help">Selected: {detailFoaFile.name}</span>
                ) : null}
              </div>
            </div>
          </div>

          <div className="programs-detail-actions-top programs-detail-actions-bottom">
            <button type="button" className="reset-filters-btn" onClick={handleCancelDetail}>
              Cancel
            </button>
            {selectedProgram.canDelete ? (
              <button
                type="button"
                className="programs-export-btn"
                onClick={handleDeleteProgram}
                disabled={deleteInProgress}
              >
                {deleteInProgress ? 'Deleting...' : 'Delete Program'}
              </button>
            ) : (
              <button
                type="button"
                className="programs-export-btn"
                onClick={handleDeactivateProgram}
                disabled={deleteInProgress || detailForm.status === 'Inactive'}
              >
                {deleteInProgress ? 'Saving...' : 'Make Inactive'}
              </button>
            )}
            <button
              type="button"
              className="programs-add-btn programs-update-btn"
              onClick={handleUpdate}
              disabled={saveInProgress || !detailForm.programCode.trim() || !detailForm.description.trim()}
            >
              {saveInProgress ? 'Saving...' : 'Update'}
            </button>
          </div>
        </section>
      )}

      {/* Add New Program modal */}
      {showForm && (
        <div
          className="programs-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Add New Program"
        >
          <div className="programs-modal">
            <h2 className="programs-modal-title">Add New Program</h2>
            <form onSubmit={handleSubmit} noValidate>
              <div className="programs-form-grid">

                <div className="programs-form-field">
                  <label className="filter-label" htmlFor="programCode">
                    Program Code <span className="required-mark">*</span>
                  </label>
                  <input
                    id="programCode"
                    name="programCode"
                    type="text"
                    className={`search-input${formErrors.programCode ? ' input-error' : ''}`}
                    value={form.programCode}
                    onChange={handleFormChange}
                    placeholder="e.g. WIOA"
                  />
                  {formErrors.programCode && (
                    <p className="field-error">{formErrors.programCode}</p>
                  )}
                </div>

                <div className="programs-form-field programs-form-field--full">
                  <label className="filter-label" htmlFor="description">
                    Program Description
                  </label>
                  <input
                    id="description"
                    name="description"
                    type="text"
                    className="search-input"
                    value={form.description}
                    onChange={handleFormChange}
                    placeholder="Full program name or description"
                  />
                </div>

                <div className="programs-form-field">
                  <label className="filter-label" htmlFor="fiscalYear">
                    Fiscal Year <span className="required-mark">*</span>
                  </label>
                  <select
                    id="fiscalYear"
                    name="fiscalYear"
                    className={`search-input${formErrors.fiscalYear ? ' input-error' : ''}`}
                    value={form.fiscalYear}
                    onChange={handleFormChange}
                  >
                    {NEW_PROGRAM_FISCAL_YEAR_OPTIONS.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                  {formErrors.fiscalYear && (
                    <p className="field-error">{formErrors.fiscalYear}</p>
                  )}
                </div>

                <div className="programs-form-field programs-form-field--full">
                  <label className="filter-label" htmlFor="program-foa-upload">
                    NOFO Document (optional)
                  </label>
                  <input
                    id="program-foa-upload"
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={(event) => setFormFoaFile(event.target.files?.[0] || null)}
                  />
                  <span className="programs-inline-help">
                    Upload a NOFO PDF and we&apos;ll extract and link it automatically when the program is created.
                  </span>
                  {formFoaFile ? (
                    <span className="programs-inline-help">Selected: {formFoaFile.name}</span>
                  ) : null}
                </div>

              </div>

              {submitError && (
                <p className="field-error programs-submit-error">{submitError}</p>
              )}

              <div className="programs-modal-actions">
                <button type="button" className="reset-filters-btn" onClick={handleCancelForm}>
                  Cancel
                </button>
                <button type="submit" className="programs-add-btn" disabled={saveInProgress}>
                  {saveInProgress ? 'Creating...' : 'Create Program'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgramsPage;
